<template>
  <div class="modal-overlay">
    <div class="modal">
      <div class="modal-header">
        <h2>{{ $t('bo.esco.mergeSkills.title') }}</h2>
        <button @click="$emit('close')" class="btn-close">&times;</button>
      </div>

      <div class="modal-body">
        <div v-if="error" class="error-banner">{{ error }}</div>

        <p class="info-text" v-html="$t('bo.esco.mergeSkills.info')"></p>

        <div class="skills-to-merge">
          <div
            v-for="skill in skills"
            :key="skill.id"
            :class="['skill-row', { 'skill-row--parent': parentId === skill.id }]"
            @click="parentId = skill.id"
          >
            <div class="skill-radio">
              <input
                type="radio"
                :value="skill.id"
                v-model="parentId"
                :id="`radio-${skill.id}`"
              />
            </div>
            <label :for="`radio-${skill.id}`" class="skill-info">
              <span class="skill-title">{{ skill.title }}</span>
              <span v-if="skill.esco_skill_type" :class="['badge-type', `badge-type-${skill.esco_skill_type}`]">
                {{ skill.esco_skill_type }}
              </span>
              <span v-if="skill.skill_group" class="skill-group">{{ skill.skill_group }}</span>
              <span v-if="parentId === skill.id" class="badge-parent">{{ $t('bo.esco.mergeSkills.keepCanonical') }}</span>
              <span v-else class="badge-merge">{{ $t('bo.esco.mergeSkills.willBeMerged') }}</span>
            </label>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button @click="$emit('close')" class="btn-cancel">{{ $t('bo.common.cancel') }}</button>
        <button
          @click="confirmMerge"
          :disabled="!parentId || loading"
          class="btn-merge"
        >
          {{ loading ? $t('bo.esco.mergeSkills.merging') : $t('bo.esco.mergeSkills.mergeInto', { count: skills.length - 1 }) }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import escoService from '@/services/escoService'

const { t } = useI18n()

const props = defineProps({
  skills: { type: Array, required: true },
})
const emit = defineEmits(['close', 'merged'])

const parentId = ref(props.skills[0]?.id || null)
const loading = ref(false)
const error = ref('')

const confirmMerge = async () => {
  if (!parentId.value) return
  loading.value = true
  error.value = ''
  try {
    await escoService.mergeSkills({
      parent_id: parentId.value,
      skill_ids: props.skills.map(s => s.id),
    })
    emit('merged')
  } catch (e) {
    error.value = e.response?.data?.message || t('bo.esco.mergeSkills.failedMerge')
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal {
  background: white;
  border-radius: 0.75rem;
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h2 {
  font-size: 1.125rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  line-height: 1;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.error-banner {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}

.info-text {
  font-size: 0.875rem;
  color: #374151;
  margin: 0;
  line-height: 1.5;
}

.skills-to-merge {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.skill-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}

.skill-row:hover { border-color: #8b5cf6; background: #faf5ff; }
.skill-row--parent { border-color: #8b5cf6; background: #faf5ff; }

.skill-radio { flex-shrink: 0; }
.skill-radio input { cursor: pointer; accent-color: #8b5cf6; }

.skill-info {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
  flex: 1;
}

.skill-title { font-weight: 500; color: #1f2937; font-size: 0.875rem; }

.skill-group {
  font-size: 0.75rem;
  color: #6b7280;
  background: #f3f4f6;
  padding: 0.1rem 0.4rem;
  border-radius: 0.25rem;
}

.badge-type {
  display: inline-block;
  padding: 0.1rem 0.4rem;
  border-radius: 0.25rem;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: capitalize;
}
.badge-type-skill { background: #dbeafe; color: #1e40af; }
.badge-type-knowledge { background: #d1fae5; color: #065f46; }
.badge-type-attitude { background: #fce7f3; color: #9d174d; }

.badge-parent {
  font-size: 0.7rem;
  font-weight: 700;
  background: #8b5cf6;
  color: white;
  padding: 0.15rem 0.5rem;
  border-radius: 9999px;
  margin-left: auto;
}

.badge-merge {
  font-size: 0.7rem;
  color: #9ca3af;
  margin-left: auto;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  border-top: 1px solid #e5e7eb;
}

.btn-cancel {
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: #374151;
}
.btn-cancel:hover { background: #f9fafb; }

.btn-merge {
  padding: 0.5rem 1.25rem;
  background: #8b5cf6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
}
.btn-merge:hover:not(:disabled) { background: #7c3aed; }
.btn-merge:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
